import { useEffect, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { ArrowForward, Check } from '@mui/icons-material';
import {
  CustomButton,
  DrawerForm,
  DrawerFormActions,
  DrawerFormActionSummary,
  DrawerFormSidebar,
} from '../../../common/components';
import ConsumptionReturnDocumentFields from './ConsumptionReturnDocumentFields';
import ConsumptionReturnOriginFields from './ConsumptionReturnOriginFields';
import ConsumptionReturnItemsTable from './ConsumptionReturnItemsTable';
import { RETURN_DRAWER_SECTIONS, RETURN_TEXTS as TEXTS } from '../constants/consumptionReturnTexts';

/** Autoriza un retorno o confirma las cantidades físicamente recibidas. */
const ConsumptionReturnDrawer = ({
  form,
  projects,
  warehouses,
  sources,
  articles,
  searchArticle,
  sourceId,
  legacyArticleId,
  onSource,
  onArticle,
  onChange,
  onAdd,
  onQuantity,
  onRemove,
  onClose,
  onSave,
  isSaving,
  error,
  readOnly,
  canSave,
}) => {
  const open = Boolean(form);
  const documentId = form?.uuid;
  const [activeSection, setActiveSection] = useState('data');
  useEffect(() => {
    if (open) setActiveSection('data');
  }, [open, documentId]);
  const title = documentId ? (readOnly ? TEXTS.view : TEXTS.receive) : TEXTS.create;
  return (
    <DrawerForm
      open={open}
      onClose={onClose}
      title={title}
      width={{ xs: '100vw', md: 'min(1200px, calc(100vw - 40px))' }}
      sidebar={(
        <DrawerFormSidebar
          title={TEXTS.sections}
          sections={RETURN_DRAWER_SECTIONS}
          activeSection={activeSection}
          onSelect={setActiveSection}
        />
      )}
      footer={(
        <DrawerFormActions
          summary={(
            <DrawerFormActionSummary
              label={TEXTS.documents}
              value={String(form?.items.length || 0)}
            />
          )}
        >
          <CustomButton
            variant="outlined"
            onClick={onClose}
            disabled={isSaving}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {readOnly ? TEXTS.close : TEXTS.cancel}
          </CustomButton>
          {activeSection === 'data' ? (
            <CustomButton
              variant="base"
              startIcon={<ArrowForward />}
              onClick={() => setActiveSection('materials')}
              disabled={isSaving}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {TEXTS.next}
            </CustomButton>
          ) : !readOnly && (
            <CustomButton
              variant="base"
              startIcon={<Check />}
              onClick={onSave}
              isLoading={isSaving}
              disabled={!canSave}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {documentId ? TEXTS.confirm : TEXTS.save}
            </CustomButton>
          )}
        </DrawerFormActions>
      )}
    >
      {form && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
          {error && (
            <Box sx={{ mx: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          {activeSection === 'data' && (
            <ConsumptionReturnDocumentFields
              form={form}
              projects={projects}
              warehouses={warehouses}
              isSaving={isSaving}
              onChange={onChange}
            />
          )}
          {activeSection === 'materials' && (
            <>
              {!documentId && (
                <ConsumptionReturnOriginFields
                  form={form}
                  sources={sources}
                  articles={articles}
                  searchArticle={searchArticle}
                  sourceId={sourceId}
                  legacyArticleId={legacyArticleId}
                  onSource={onSource}
                  onArticle={onArticle}
                  onChange={onChange}
                  onAdd={onAdd}
                  isSaving={isSaving}
                />
              )}
              <ConsumptionReturnItemsTable
                items={form.items}
                onQuantity={onQuantity}
                onRemove={onRemove}
                readOnly={readOnly || isSaving}
                receiving={Boolean(documentId)}
              />
            </>
          )}
        </Box>
      )}
    </DrawerForm>
  );
};

export default ConsumptionReturnDrawer;
